import { Coordinates, ImportChoiceItem, Mark } from '@/types/commons/commons.types';
import { ProcedureModel } from '@/types/config/procedure.types';
import { ActiveGlobalScreenConfiguration, GlobalScreenConfiguration } from '@/types/config/screenConfiguration.types';
import { Flight, Vertiport } from '@/types/intercom/intercom.types';
import { Monitoring } from '@/types/sensor/monitoring.types';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

import { GroupWithUsers, Profile } from '../authent/groups.types';
import { AuthentSettings, LogoutCauseEnum, User } from '../authent/login.types';
import { Role } from '../authent/roles.types';
import { Site } from '../authent/sites.types';
import { Contact, Message } from '../chat/chat.types';
import {
  InitializationMaskingZone,
  NoFlyZone,
  NoFlyZone3d,
  NoInitZone,
  NoNeutralizationZone,
  Organization,
  Platform,
  ZoneOfInterest,
} from '../config/config.types';
import { GridConfiguration, GridLayout } from '../config/gridLayout.types';
import { Home } from '../config/home.types';
import { MergingConfiguration } from '../config/merging.types';
import { PointOfInterest } from '../config/pointOfInterest.types';
import { RoundConfiguration } from '../config/round.types';
import { NimbleConfiguration } from '../config/videoServer.types';
import { VideoStream } from '../config/videoStream.types';
import { NotificationType } from '../data/data.types';
import { Period, ReplayEvent, ReplayStorageConfiguration } from '../replay/replay.types';
import { Classification } from '../sensor/classification.types';
import { SensorConfiguration } from '../sensor/configuration.types';
import { Identification, ReliabilityEnum } from '../sensor/identification.types';
import {
  AbstractLocation,
  CollaborativeData,
  Plot,
  PlotLight,
  PolygonLocation,
  StrobeLocation,
  TargetLocation,
  TechnicalData,
} from '../sensor/sensor.types';
import {
  AutomatonSegmentTypeEnum,
  CameraIrAlgoEnum,
  EquipmentConfiguration,
  IsoModeEnum,
  JamTypeEnum,
  SensorTypeEnum,
  SystemModeEnum,
  TrackingStatusEnum,
  TrackLight,
  WhiteBalanceModeEnum,
} from '../sensor/sensors';
import { SensorStorageConfiguration } from '../sensor/storage.types';
import { Drone, DroneClearanceEnum, StrategicDrone } from '../utm/drone.types';
import { Fleet } from '../utm/fleet.type';
import { AbstractFlightPlan } from '../utm/flightPlan.types';
import { FlyZone } from '../utm/flyZone.types';
import { Pilot } from '../utm/pilot.types';
import { FlyRoute, FlySection } from '../utm/route.types';
import { HoloLog } from './logs.type';

export enum TargetStatusEnum {
  ONGOING = 'ONGOING',
  LOST = 'LOST',
}

export enum TrackStatusEnum {
  RUNNING = 'RUNNING',
  STOPPED = 'STOPPED',
  TO_IGNORE = 'TO_IGNORE',
  TO_BE_PURGED = 'TO_BE_PURGED',
}

export enum TacticalClearanceEnum {
  NOTHING_SIGNIFICANT = 'NOTHING_SIGNIFICANT',
  VERIFIED = 'VERIFIED',
  UNDER_OBSERVATION = 'UNDER_OBSERVATION',
  UNDER_SUPERVISION = 'UNDER_SUPERVISION',
  UNDER_INTERVENTION = 'UNDER_INTERVENTION',
}

export interface Target<T extends AbstractLocation> {
  id: string;
  uuid: string;
  displayId: string | null;
  site: string;
  appCode: string;
  status: TargetStatusEnum;
  mainTrackId: string;

  trackIds: TrackLight[];
  trajectory: PlotLight<T>[];
  identification: Identification;
  classification: Classification;
  tacticalClearance: TacticalClearanceEnum;
  lastPosition: T extends TargetLocation ? PlotLight<T> : Plot<T> | null;
  technicalData: TechnicalData | null;

  detectionStart: string;
  detectionEnd: string;
  safetyDistance: number | null;
  criticalDistance: number | null;
  displayCollisionDiscs: boolean;

  targetGroup: string | null;
  droneCode: string | null;
  fpCodes: string[]; // full fp list
  hasOngoingFlightPlan: boolean;
  marks: Mark[];

  reliability: ReliabilityEnum;
  clearance: DroneClearanceEnum | null;
  maxSpeed: number | null;
  maxTransmittedAgl: number | null;
  maxComputedAgl: number | null;
  maxTransmittedAsl: number | null;
  maxComputedAsl: number | null;
  collaborativeData: CollaborativeData | null;
}

export interface Track<T extends AbstractLocation> {
  id: string;
  uuid: string;
  site: string;
  sensorCode: string;
  subSensorCode: string;
  sensorType: SensorTypeEnum;
  appCode: string;

  status: TrackStatusEnum;
  identification: Identification;
  classification: Classification;
  tacticalClearance: TacticalClearanceEnum | null;
  lastPosition: Plot<T> | null;
  firstDetection: string;
  lastDetection: string;
  marks: Mark[];
  uniqueId: string;
  uniqueKey: string;
  plots: PlotLight<T>[];
}

export interface RawSituation {
  rawPlots: PlotLight<AbstractLocation>[];
  tracks: Track<AbstractLocation>[];
}

export interface TacticalSituation {
  strobes: Target<StrobeLocation>[];
  targets: Target<TargetLocation>[];
  polygons?: Target<PolygonLocation>[];
  infos: MergingInfo;
  monitoring: Monitoring;
}

export interface MergingInfo {
  mergingTime: number;
  preprocessTime: number;
  covarianceTime: number;
  targetTime: number;
  notificationTime: number;
  sensorInfos: Record<string, SensorMergingInfo>;
}

export interface SensorMergingInfo {
  receivedPlots: number;
  tracks: number;
}

export interface SituationMessage {
  version: number;
  json: string;
  fullSituation: boolean;
}

export interface GlobalConfiguration {
  items: ImportChoiceItem[];
  metadata: null;
  modelVersion: string;
  //======GLOBAL=====\\
  logs: HoloLog[];
  //======AUTHENT=====\\
  profiles: Profile[];
  users: User[];
  roles: Role[];
  sites: Site[];
  authentSettings: AuthentSettings | null;
  groups: GroupWithUsers[];
  activeUser: User;
  forcedLogoutCause: LogoutCauseEnum;
  //======CHAT======\\
  contacts: Contact[];
  messages: Message[];
  //======CONFIG======\\
  home: Home;
  noFlyZones: NoFlyZone[];
  noFlyZones3d: NoFlyZone3d[];
  initializationMaskingZones: InitializationMaskingZone[];
  noNeutralizationZones: NoNeutralizationZone[];
  pointOfInterests: PointOfInterest[];
  zoneOfInterests: ZoneOfInterest[];
  noInitZones: NoInitZone[];
  mergingConfiguration: MergingConfiguration | null;
  videoStreams: VideoStream[];
  videoConfiguration: NimbleConfiguration | null;
  layouts: GridLayout[];
  gridConfigurations: GridConfiguration[];
  globalScreenConfigurations: GlobalScreenConfiguration[];
  roundConfigurations: RoundConfiguration[];
  organizations: Organization[];
  platforms: Platform[];
  procedureModels: ProcedureModel[];
  activeGlobalScreenConfiguration: ActiveGlobalScreenConfiguration | null;
  //======REPLAY======\\
  replayEvents: ReplayEvent[];
  replayManualPeriods: Period[];
  replayStorageConfiguration: ReplayStorageConfiguration | null;
  replaySettings: ReplaySettings;
  //=======SENSOR=====\\
  perimeterCameras: PerimeterCamera[];
  sensors: SensorConfiguration[];
  sensorStorage: SensorStorageConfiguration | null;
  //========UTM=======\\
  drones: Drone[];
  strategicDrones: StrategicDrone[];
  pilots: Pilot[];
  flightPlans: AbstractFlightPlan[];
  fleets: Fleet[];
  flyRoutes: FlyRoute[];
  flySections: FlySection[];
  flyZones: FlyZone[];
  equipments: EquipmentConfiguration[];
  //========DATA==========\\
  ongoingNotifications: NotificationType[];
  //========INTERCOM==========\\
  vertiports: Vertiport[];
  flights: Flight[];
}

export interface AutomatonCommand {
  type: AutomatonCommandEnum;
  sensorCode: string;
}

export interface AutomatonSleepAllCabinetsCommand extends AutomatonCommand {
  type: AutomatonCommandEnum.AUTOMATON_COMMAND_SLEEP_ALL_CABINETS;
  sleep: boolean;
}

export interface AutomatonCabinetCommand extends AutomatonCommand {
  cabinetId: number;
}

export interface AutomatonDefenceCommand extends AutomatonCommand {
  type: AutomatonCommandEnum.AUTOMATON_COMMAND_DEFENCE;
  defence: boolean;
}

export interface AutomatonCabinetDefenceCommand extends AutomatonCabinetCommand {
  type: AutomatonCommandEnum.AUTOMATON_CABINET_COMMAND_DEFENCE;
  defence: boolean;
}

export interface AutomatonSegmentDefenceCommand extends AutomatonCabinetCommand {
  type: AutomatonCommandEnum.AUTOMATON_SEGMENT_COMMAND_DEFENCE;
  segmentId: number; // id de n'importe quel segment, haut ou bas, tant que le Haut/Bas est précisé dans le segmentType
  defence: boolean;
  segmentType: AutomatonSegmentTypeEnum;
}

export interface AutomatonSectionDefenceCommand extends AutomatonCabinetCommand {
  type: AutomatonCommandEnum.AUTOMATON_SECTION_COMMAND_DEFENCE;
  segmentId: number; // Peu importe haut ou bas, le back prendra les 2 de la section dont est issu ce segment
  defence: boolean;
}

export interface AutomatonSleepCommand extends AutomatonCabinetCommand {
  type: AutomatonCommandEnum.AUTOMATON_COMMAND_SLEEP;
  sleep: boolean;
}

export interface AutomatonReinitializeCommand extends AutomatonCabinetCommand {
  type: AutomatonCommandEnum.AUTOMATON_COMMAND_REINITIALIZE;
  reinitialize: boolean;
}

export enum AutomatonCommandEnum {
  AUTOMATON_SECTION_COMMAND_DEFENCE = 'AutomatonSectionDefence',
  AUTOMATON_SEGMENT_COMMAND_DEFENCE = 'AutomatonSegmentDefence',
  AUTOMATON_CABINET_COMMAND_DEFENCE = 'AutomatonCabinetDefence',
  AUTOMATON_COMMAND_DEFENCE = 'AutomatonDefence',
  AUTOMATON_COMMAND_SLEEP = 'AutomatonSleep',
  AUTOMATON_COMMAND_SLEEP_ALL_CABINETS = 'AutomatonSleepAllCabinets',
  AUTOMATON_COMMAND_REINITIALIZE = 'AutomatonReinitialize',
}

export interface CameraCommand {
  type: CameraCommandEnum;
  sensorCode: string;
  cameraOperatorUuid: string | null;
}

export enum CameraCommandEnum {
  CAMERA_COMMAND_AUTO_ZOOM = 'CameraCommandAutoZoom',
  CAMERA_COMMAND_SET_ZOOM = 'CameraCommandSetZoom',
  CAMERA_COMMAND_SET_FOV = 'CameraCommandSetFov',
  CAMERA_COMMAND_ZOOM = 'CameraCommandZoom',
  CAMERA_COMMAND_ZOOM_SYNC = 'CameraCommandSyncZoom',
  CAMERA_COMMAND_CAMERA_ACTION = 'CameraCommandCameraAction',
  CAMERA_COMMAND_FOCUS = 'CameraCommandFocus',
  CAMERA_COMMAND_SET_FOCUS = 'CameraCommandSetFocus',
  CAMERA_COMMAND_HYBRID_FOCUS = 'CameraCommandHybridFocus',
  CAMERA_COMMAND_LOOK_AT = 'CameraCommandLookAt',
  CAMERA_COMMAND_MOVE = 'CameraCommandMove',
  CAMERA_COMMAND_SIMPLE_ROUND = 'CameraCommandSimpleRound',
  CAMERA_COMMAND_RELEASE_TOKEN = 'CameraCommandReleaseToken',
  CAMERA_COMMAND_REQUEST_TOKEN = 'CameraCommandRequestToken',
  CAMERA_COMMAND_POINT_AT = 'CameraCommandPointAt',
  CAMERA_COMMAND_POINT_AT_PIXEL = 'CameraCommandPointAtPixel',
  CAMERA_COMMAND_VERTICAL_SCAN_START = 'CameraCommandVerticalScanStart',
  CAMERA_COMMAND_VERTICAL_SCAN_STOP = 'CameraCommandVerticalScanStop',
  CAMERA_COMMAND_SYSTEM_MODE = 'CameraCommandSystemMode',
  CAMERA_COMMAND_SET_PRESET = 'CameraCommandSetPreset',
  CAMERA_COMMAND_CALL_PRESET = 'CameraCommandCallPreset',
  CAMERA_COMMAND_CLEAR_PRESET = 'CameraCommandClearPreset',
  CAMERA_COMMAND_SET_HOME = 'CameraCommandSetHome',
  CAMERA_COMMAND_CALL_HOME = 'CameraCommandCallHome',
  CAMERA_COMMAND_STOP_ALL_TRACKINGS = 'CameraCommandStopAllTrackings',
  CAMERA_COMMAND_SENSOR_TRACKING = 'CameraCommandSensorTracking',
  CAMERA_COMMAND_VIDEO_TRACKING = 'CameraCommandVideoTracking',
  CAMERA_COMMAND_CROP = 'CameraCommandCrop',
  CAMERA_COMMAND_EXPOSURE_MODE = 'CameraCommandSetExposureMode',
  CAMERA_COMMAND_ISO_MODE = 'CameraCommandSetIso',
  CAMERA_COMMAND_WHITER_BALANCE = 'CameraCommandSetWhiteBalance',
  CAMERA_COMMAND_SET_POLARITY = 'CameraCommandSetPolarity',
  CAMERA_COMMAND_REBOOT_CHARM = 'CameraCommandRebootCharm',
  CAMERA_COMMAND_DEICING = 'CameraCommandDeIcing',
  CAMERA_COMMAND_NUC = 'CameraCommandNuc',
  CAMERA_COMMAND_VIDEO_POWER = 'CameraCommandVideoPower',
  CAMERA_COMMAND_GET_IR_CONFIG = 'CameraCommandGetIrConfig',
  CAMERA_COMMAND_SET_IR_CONFIG = 'CameraCommandSetIrConfig',
  CAMERA_COMMAND_RESET_IR_CONFIG = 'CameraCommandResetIrConfig',
  CAMERA_COMMAND_TOGGLE_IR_CONFIG = 'CameraCommandToggleIrConfig',
  CAMERA_COMMAND_ACTIVATE_IR = 'CameraCommandActivateIr',
}

export interface XentaCommand {
  type: XentaCommandEnum;
  sensorCode: string;
}

export enum XentaCommandEnum {
  COMMAND_ANTENNA_SURVEILLANCE = 'AntennaSurveillance',
  COMMAND_ANTENNA_STOP_AND_STARE_AZIMUTH = 'AntennaStopAndStareAzimuth',
  COMMAND_PSR_TX_ON = 'PsrTxOn',
  // Voir XentaCommand.java si besoin de rajouter les autres commandes
}

export enum XentaSurveillanceModeEnum {
  PROFILE_1 = '0',
  PROFILE_2 = '1',
  PROFILE_3 = '2',
  PROFILE_4 = '3',
}

export interface XentaRotationCommand extends XentaCommand {
  type: XentaCommandEnum.COMMAND_ANTENNA_SURVEILLANCE;
  mode: XentaSurveillanceModeEnum.PROFILE_1;
}

export const XENTA_INITIAL_AZIMUTH = 0.0; // The azimuth we want the radar to come back before stopping the rotation

export interface XentaStopRotationCommand extends XentaCommand {
  type: XentaCommandEnum.COMMAND_ANTENNA_STOP_AND_STARE_AZIMUTH;
  azimuth: number;
}

export interface XentaEmissionCommand extends XentaCommand {
  type: XentaCommandEnum.COMMAND_PSR_TX_ON;
  on: boolean;
}

export enum FocusDirectionEnum {
  IN = 'IN',
  OUT = 'OUT',
  STOP = 'STOP',
  AUTO_FOCUS = 'AUTO_FOCUS',
}

export enum ZoomDirectionEnum {
  IN = 'IN',
  OUT = 'OUT',
  STOP = 'STOP',
}

export enum CameraObjectSizeEnum {
  TARGET = 40,
  PERIMETER = 150,
}

export interface VideoBox {
  x: number;
  y: number;
  height: number;
  width: number;
}

export interface CameraVideoCommand extends CameraCommand {
  cameraId: number;
}

export interface CameraCommandAutoZoom extends CameraVideoCommand {
  distance: number;
}

export interface CameraCommandSetZoom extends CameraVideoCommand {
  zoom: number;
}

export interface CameraCommandSetFov extends CameraVideoCommand {
  fov: number;
}

export interface CameraCommandSetFocus extends CameraVideoCommand {
  focus: number;
}

export interface CameraCommandFocus extends CameraVideoCommand {
  direction: FocusDirectionEnum;
}

export interface CameraCommandZoom extends CameraVideoCommand {
  direction: ZoomDirectionEnum;
}

export interface CameraCommandZoomSync extends CameraVideoCommand {
  zoom: number;
  fov: number;
}

export interface CameraCommandVideoTracking extends CameraVideoCommand {
  box: VideoBox | null;
  mode: TrackingStatusEnum;
  id: string | null;
}

export interface CameraCommandVideoPower extends CameraVideoCommand {
  on: boolean;
}

export type CameraCommandGetIrConfig = CameraVideoCommand;

export interface CameraCommandSetIrConfig extends CameraVideoCommand {
  gain: number;
  offset: number;
  gamma: number;
}

export type CameraCommandResetIrConfig = CameraVideoCommand;

export interface CameraCommandToggleIrConfig extends CameraVideoCommand {
  algo: CameraIrAlgoEnum;
}

export interface CameraCommandActivateIr extends CameraVideoCommand {
  on: boolean;
}

export interface CameraCommandMove extends CameraCommand {
  panSpeed: number; //horizontal
  tiltSpeed: number; //vertical
}

export interface CameraCommandSimpleRound extends CameraCommand {
  roundOn: boolean;
  speed?: number;
}

export interface CameraCommandRequestToken extends CameraCommand {
  userName: string;
  userLogin: string;
}

export interface CameraCommandReleaseToken extends CameraCommand {
  userLogin: string;
}

export interface CameraCommandSetPreset extends CameraCommand {
  preset: number;
}

export interface CameraCommandCallPreset extends CameraCommand {
  preset: number;
}

export interface CameraCommandClearPreset extends CameraCommand {
  preset: number;
}

export type CameraCommandSetHome = CameraCommand;

export type CameraCommandCallHome = CameraCommand;

export interface CameraCommandLookAt extends CameraCommand {
  position: Coordinates;
  segmentStart: Coordinates;
  segmentEnd: Coordinates;
  altitude: number;
  objectSize: number;
}

export interface CameraCommandPointAt extends CameraVideoCommand {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CameraCommandPointAtPixel extends CameraCommand {
  imageWidth: number;
  imageHeight: number;
  clickPositionX: number;
  clickPositionY: number;
}

export interface CameraCommandSensorTracking extends CameraCommand {
  trackedTargetId: string;
  targetFirstPosition: Coordinates;
}

export interface CameraCommandStopAllTrackings extends CameraCommand {
  trackedTargetId: string;
}

export interface CameraCommandSetExposureMode extends CameraVideoCommand {
  cameraId: number;
  exposureMode: ExposureModeEnum;
}

export interface CameraCommandSetIso extends CameraVideoCommand {
  cameraId: number;
  iso: IsoModeEnum;
}

export interface CameraCommandSetWhiteBalance extends CameraVideoCommand {
  cameraId: number;
  whiteBalance: WhiteBalanceModeEnum;
}

export interface CameraCommandSetPolarity extends CameraVideoCommand {
  cameraId: number;
  black: boolean;
}

export interface CameraCommandHybridFocus extends CameraVideoCommand {
  cameraId: number;
  focusLevel: number;
}

export type CameraCommandRebootCharm = CameraCommand;

export enum GroundStationCommandEnum {
  ARM = 'Arm',
  TAKEOFF = 'Takeoff',
  SET_MODE = 'SetMode',
  LAND = 'Land',
  GO_TO = 'GoTo',
  FIRE = 'Fire',
}

export interface BaseGroundStationCommand {
  sensorCode: string;
  systemId: number;
  componentId: number;
}

export interface ArmCommand extends BaseGroundStationCommand {
  type: GroundStationCommandEnum.ARM;
  arm: boolean;
}

export interface TakeoffCommand extends BaseGroundStationCommand {
  type: GroundStationCommandEnum.TAKEOFF;
  height: number;
}

export interface SetModeCommand extends BaseGroundStationCommand {
  type: GroundStationCommandEnum.SET_MODE;
  mode: GroundStationModeEnum;
}

export interface GoToCommand extends BaseGroundStationCommand {
  type: GroundStationCommandEnum.GO_TO;
  latitude: number;
  longitude: number;
  height: number;
  speed: number | null;
}

export interface LandCommand extends BaseGroundStationCommand {
  type: GroundStationCommandEnum.LAND;
}

export interface FireCommand extends BaseGroundStationCommand {
  type: GroundStationCommandEnum.FIRE;
  targetId: string | null;
  jamType: JamTypeEnum[];
}

export type GroundStationCommand =
  | ArmCommand
  | TakeoffCommand
  | SetModeCommand
  | GoToCommand
  | LandCommand
  | FireCommand;

export interface CameraCommandDeIcing extends CameraVideoCommand {
  cameraId: number;
  on: boolean;
}

export interface CameraCommandSystemMode extends CameraCommand {
  systemMode: SystemModeEnum;
}

export interface CameraCommandNuc extends CameraVideoCommand {
  cameraId: number;
}

export enum GroundStationModeEnum {
  GUIDED = 'GUIDED',
  LAND = 'LAND',
}

export interface HolodroneCommand {
  type: HolodroneCommandEnum;
  sensorCode: string;
}

export interface RebootHolodroneCommand extends HolodroneCommand {
  type: HolodroneCommandEnum.RebootHolodrone;
}

export interface RestartHolodroneCommand extends HolodroneCommand {
  type: HolodroneCommandEnum.RestartHolodrone;
}

export enum HolodroneCommandEnum {
  RebootHolodrone = 'RebootHolodrone',
  RestartHolodrone = 'RestartHolodrone',
}

interface CommonSkyJackerCommand {
  sensorCode: string;
  stationId: string | null;
}

export enum SkyJackerCommandEnum {
  SkyJackerGeoDesignation = 'SkyJackerGeoDesignation',
  SkyJackerSensorTracking = 'SkyJackerSensorTracking',
  SkyJackerSetEmission = 'SkyJackerSetEmission',
}

export interface SkyJackerGeoDesignationCommand extends CommonSkyJackerCommand {
  type: SkyJackerCommandEnum.SkyJackerGeoDesignation;
  latitude: number;
  longitude: number;
  altitude: number | null;
}

export interface SkyJackerSensorTrackingCommand extends CommonSkyJackerCommand {
  type: SkyJackerCommandEnum.SkyJackerSensorTracking;
  trackedTargetId: string | null;
}

export interface SkyJackerSetEmissionCommand extends CommonSkyJackerCommand {
  type: SkyJackerCommandEnum.SkyJackerSetEmission;
  emitting: boolean;
}

export type SkyJackerCommand =
  | SkyJackerGeoDesignationCommand
  | SkyJackerSensorTrackingCommand
  | SkyJackerSetEmissionCommand;

export interface C2StorageConfiguration {
  site: string;
  replayConfiguration?: ReplayStorageConfiguration;
  sensorConfiguration?: SensorStorageConfiguration;
  videoConfiguration?: NimbleConfiguration;
}

export type ReplaySettings = {
  id: number;
  videoLatencyMargin: number;
  videoBaseSequenceDuration: number;
};

export enum ExposureModeEnum {
  EXPOSURE_MODE_UNSPECIFIED = '0',
  EXPOSURE_MODE_MANUAL_EXPOSURE_M = '1',
  EXPOSURE_MODE_PROGRAM_AUTO_P = '2',
  EXPOSURE_MODE_APERTURE_PRIORITY_A = '3',
  EXPOSURE_MODE_SHUTTER_PRIORITY_S = '4',
  EXPOSURE_MODE_INTELLIGENT_AUTO = '5',
  EXPOSURE_MODE_SUPERIOR_AUTO = '6',
  EXPOSURE_MODE_MOVIE_PROGRAM_AUTO_P = '7',
  EXPOSURE_MODE_MOVIE_APERTURE_PRIORITY_A = '8',
  EXPOSURE_MODE_MOVIE_SHUTTER_PRIORITY_S = '9',
  EXPOSURE_MODE_MOVIE_MANUAL_EXPOSURE_M = '10',
  EXPOSURE_MODE_MOVIE_INTELLIGENT_AUTO = '11',
  EXPOSURE_MODE_MOVIE_HFR_P = '12',
  EXPOSURE_MODE_MOVIE_HFR_A = '13',
  EXPOSURE_MODE_MOVIE_HFR_S = '14',
  EXPOSURE_MODE_MOVIE_HFR_M = '15',
}

export enum EquipmentCommandTypeEnum {
  SET_WR_ACTUATOR_STATE = 'SET_WR_ACTUATOR_STATE',
}

export interface CommonEquipmentCommand {
  equipmentCode: string;
}

export enum WebRelayActuatorStateEnum {
  ON = 'ON',
  OFF = 'OFF',
}

export interface SetWebRelayActuatorStateCommand extends CommonEquipmentCommand {
  type: EquipmentCommandTypeEnum.SET_WR_ACTUATOR_STATE;
  actuatorState: WebRelayActuatorStateEnum;
}

export type EquipmentCommand = SetWebRelayActuatorStateCommand;

type NeutralEngagement =
  | {
      targetId: null;
      coordinates: null;
    }
  | {
      targetId: string;
      coordinates: Coordinates;
    }
  | {
      targetId: null;
      coordinates: Coordinates;
    };

type EngagementSequence = {
  id: string;
  startTime: number;
  jammingDuration: number;
  sensorCodes: string[];
};

export type NeutralInfo = {
  engagementSequenceDto: EngagementSequence | null;
  jamming: boolean;
  sensorCode: string | null;
  appCode: string | null;
  site: string | null;
  login: string | null;
} & NeutralEngagement;

export enum Mc2CommandEnum {
  SetSectorMode = 'SetSectorMode',
  MajesJamming = 'MajesJamming',
  Dfb6Jamming = 'Dfb6Jamming',
  BlastJamming = 'BlastJamming',
  StopJamming = 'StopJamming',
  ChangeConfiguration = 'ChangeConfiguration',
}

export interface Mc2Command {
  type: Mc2CommandEnum;
  sensorCode: string;
}

export interface MajesSetSectorModeCommand extends Mc2Command {
  type: Mc2CommandEnum.SetSectorMode;
  sectorMode: number;
}

export interface Dfb6ChangeConfigurationCommand extends Mc2Command {
  type: Mc2CommandEnum.ChangeConfiguration;
  configuration?: string | null;
  targetId?: string | null;
}

export interface Mc2JammingCommand extends Mc2Command {
  admin: boolean;
  forced: boolean;
}

export interface MajesJammingCommand extends Mc2JammingCommand {
  type: Mc2CommandEnum.MajesJamming;
  sectorMode: number;
  modules: Record<number, boolean>;
}

export interface Dfb6JammingCommand extends Mc2JammingCommand {
  type: Mc2CommandEnum.Dfb6Jamming;
  sectorMode: number;
  modules: Record<number, boolean>;
}

export interface BlastJammingCommand extends Mc2JammingCommand {
  type: Mc2CommandEnum.BlastJamming;
  jammingMode: number;
}

export interface StopJammingCommand extends Mc2JammingCommand {
  type: Mc2CommandEnum.StopJamming;
}

export enum FlirCommandEnum {
  CALIBRATE = 'CALIBRATE',
  CALIBRATE_AZ = 'CALIBRATE_AZ',
  CALIBRATE_POS = 'CALIBRATE_POS',
  RESET = 'RESET',
  STBY = 'STBY',
  XMIT = 'XMIT',
}

export interface FlirCommand {
  type: FlirCommandEnum;
  sensorCode: string;
}
